import React from 'react';
import { motion } from 'framer-motion';
interface CircleVisualProps {
  radius?: number;
  diameter?: number;
  circumference?: number;
  area?: number;
  label?: string;
  showFormula?: boolean;
  highlightRadius?: boolean;
  highlightDiameter?: boolean;
  sectorAngle?: number;
  highlightSector?: boolean;
  mode?: 'stimulus' | 'support';
}
/**
 * Helper: build an SVG arc path for a sector from 0° to `angleDeg`.
 * The sector starts at 12-o'clock (−90°) and sweeps clockwise.
 */
function sectorPath(cx: number, cy: number, r: number, angleDeg: number): string {
  if (angleDeg >= 360) {
    // Full circle — use two arcs
    return `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx} ${cy + r} A ${r} ${r} 0 1 1 ${cx} ${cy - r} Z`;
  }
  const startRad = -Math.PI / 2; // 12 o'clock
  const endRad = startRad + angleDeg * Math.PI / 180;
  const largeArc = angleDeg > 180 ? 1 : 0;
  const x1 = cx + r * Math.cos(startRad);
  const y1 = cy + r * Math.sin(startRad);
  const x2 = cx + r * Math.cos(endRad);
  const y2 = cy + r * Math.sin(endRad);
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
}
export function CircleVisual({
  radius: propRadius,
  diameter: propDiameter,
  circumference,
  area,
  label,
  showFormula = false,
  highlightRadius = false,
  highlightDiameter = false,
  sectorAngle,
  highlightSector = false,
  mode = 'support'
}: CircleVisualProps) {
  // Calculate missing values
  const radius = propRadius ?? (propDiameter ? propDiameter / 2 : 50);
  const diameter = propDiameter ?? radius * 2;
  // SVG dimensions
  const svgSize = 280;
  const cx = svgSize / 2;
  const cy = svgSize / 2;
  // Scale circle to fill SVG nicely — ensure minimum visible size
  const maxDisplayRadius = svgSize * 0.38; // ~106px
  const minDisplayRadius = 40;
  const maxExpectedRadius = 20;
  const scaleFactor = maxDisplayRadius / maxExpectedRadius;
  const circleRadius = Math.max(minDisplayRadius, Math.min(maxDisplayRadius, radius * scaleFactor));
  const hasSector = typeof sectorAngle === 'number' && sectorAngle > 0 && sectorAngle < 360;
  return <div className="flex flex-col items-center gap-4 py-3" data-id="element-11506">
      {label && <p className="text-sm font-semibold text-slate-600 text-center" data-id="element-11507">
          {label}
        </p>}

      {/* Circle SVG */}
      <motion.svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`} initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.4
    }} className="drop-shadow-md" role="img" aria-label={hasSector ? `Cirkel met sector van ${sectorAngle}°` : 'Cirkel'} data-id="element-11508">
        {/* Full circle background */}
        <motion.circle cx={cx} cy={cy} r={circleRadius} fill={hasSector ? 'rgb(241, 245, 249)' : 'rgb(191, 219, 254)'} stroke="rgb(59, 130, 246)" strokeWidth="3" initial={{
        pathLength: 0
      }} animate={{
        pathLength: 1
      }} transition={{
        duration: 1,
        ease: 'easeInOut'
      }} data-id="element-11509" />

        {/* Sector wedge (when sectorAngle is provided) */}
        {hasSector && <motion.path d={sectorPath(cx, cy, circleRadius, sectorAngle)} fill="rgb(147, 197, 253)" stroke="rgb(59, 130, 246)" strokeWidth="2.5" strokeLinejoin="round" initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.3,
        duration: 0.5,
        ease: 'easeOut'
      }} style={{
        transformOrigin: `${cx}px ${cy}px`
      }} data-id="element-11510" />}

        {/* Sector angle arc indicator */}
        {hasSector && sectorAngle <= 180 && (() => {
        const arcR = circleRadius * 0.25;
        const startRad = -Math.PI / 2;
        const endRad = startRad + sectorAngle * Math.PI / 180;
        const largeArc = sectorAngle > 180 ? 1 : 0;
        const ax1 = cx + arcR * Math.cos(startRad);
        const ay1 = cy + arcR * Math.sin(startRad);
        const ax2 = cx + arcR * Math.cos(endRad);
        const ay2 = cy + arcR * Math.sin(endRad);
        return <path d={`M ${ax1} ${ay1} A ${arcR} ${arcR} 0 ${largeArc} 1 ${ax2} ${ay2}`} fill="none" stroke="rgb(99, 102, 241)" strokeWidth="2" strokeDasharray="4,3" data-id="element-11511" />;
      })()}

        {/* Sector angle label */}
        {hasSector && (() => {
        const labelR = circleRadius * 0.35;
        const midAngleRad = -Math.PI / 2 + sectorAngle * Math.PI / 360;
        const lx = cx + labelR * Math.cos(midAngleRad);
        const ly = cy + labelR * Math.sin(midAngleRad);
        return <text x={lx} y={ly} textAnchor="middle" dominantBaseline="central" className="text-sm font-bold fill-indigo-700" data-id="element-11512">
                {sectorAngle}°
              </text>;
      })()}

        {/* Center point */}
        <circle cx={cx} cy={cy} r="4" fill="rgb(59, 130, 246)" data-id="element-11513" />

        {/* Radius line (if highlighted) */}
        {highlightRadius && <>
            <motion.line x1={cx} y1={cy} x2={cx + circleRadius} y2={cy} stroke="rgb(239, 68, 68)" strokeWidth="3" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          delay: 0.5,
          duration: 0.6
        }} data-id="element-11514" />
            <text x={cx + circleRadius / 2} y={cy - 10} textAnchor="middle" className="text-sm font-bold fill-red-600" data-id="element-11515">
              r = {radius}
            </text>
          </>}

        {/* Diameter line (if highlighted) */}
        {highlightDiameter && <>
            <motion.line x1={cx - circleRadius} y1={cy} x2={cx + circleRadius} y2={cy} stroke="rgb(34, 197, 94)" strokeWidth="3" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          delay: 0.5,
          duration: 0.6
        }} data-id="element-11516" />
            <text x={cx} y={cy + 25} textAnchor="middle" className="text-sm font-bold fill-green-600" data-id="element-11517">
              d = {diameter}
            </text>
          </>}
      </motion.svg>

      {/* Formula display */}
      {mode === 'support' && showFormula && <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 space-y-4 max-w-md w-full" data-id="element-11518">
          {circumference !== undefined && <div className="flex flex-col gap-1" data-id="element-11519">
              <span className="text-sm font-semibold text-blue-900 border-b border-blue-200 pb-1 mb-1" data-id="element-11520">
                Omtrek (C)
              </span>
              <div className="flex justify-between items-center" data-id="element-11521">
                <span className="text-sm text-blue-800" data-id="element-11522">Formule:</span>
                <span className="font-mono font-bold text-blue-700" data-id="element-11523">
                  C = 2πr
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-11524">
                <span className="text-sm text-blue-800" data-id="element-11525">Invullen:</span>
                <span className="font-mono font-bold text-blue-700" data-id="element-11526">
                  2 × π × {radius}
                </span>
              </div>
              <div className="flex justify-between items-center bg-blue-100 p-2 rounded-md mt-1" data-id="element-11527">
                <span className="text-sm font-bold text-blue-900" data-id="element-11528">
                  Uitkomst:
                </span>
                <span className="font-mono font-black text-blue-800" data-id="element-11529">
                  ≈ {circumference.toFixed(1)} cm
                </span>
              </div>
            </div>}
          {area !== undefined && <div className="flex flex-col gap-1" data-id="element-11530">
              <span className="text-sm font-semibold text-blue-900 border-b border-blue-200 pb-1 mb-1" data-id="element-11531">
                Oppervlakte (A)
              </span>
              <div className="flex justify-between items-center" data-id="element-11532">
                <span className="text-sm text-blue-800" data-id="element-11533">Formule:</span>
                <span className="font-mono font-bold text-blue-700" data-id="element-11534">
                  A = πr²
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-11535">
                <span className="text-sm text-blue-800" data-id="element-11536">Invullen:</span>
                <span className="font-mono font-bold text-blue-700" data-id="element-11537">
                  π × {radius}²
                </span>
              </div>
              <div className="flex justify-between items-center bg-blue-100 p-2 rounded-md mt-1" data-id="element-11538">
                <span className="text-sm font-bold text-blue-900" data-id="element-11539">
                  Uitkomst:
                </span>
                <span className="font-mono font-black text-blue-800" data-id="element-11540">
                  ≈ {area.toFixed(1)} cm²
                </span>
              </div>
            </div>}
          {!circumference && !area && <div className="space-y-1" data-id="element-11541">
              <p className="text-sm text-blue-900" data-id="element-11542">
                <span className="font-bold" data-id="element-11543">Omtrek:</span> C = 2πr of C = πd
              </p>
              <p className="text-sm text-blue-900" data-id="element-11544">
                <span className="font-bold" data-id="element-11545">Oppervlakte:</span> A = πr²
              </p>
            </div>}
        </div>}

      {/* Sector info card (when sector is shown) */}
      {hasSector && mode === 'support' && <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-5 py-3 shadow-sm" data-id="element-11546">
          <div className="flex flex-col gap-1" data-id="element-11547">
            <div className="flex items-center gap-2" data-id="element-11548">
              <span className="text-sm font-semibold text-indigo-900" data-id="element-11549">
                Sector:
              </span>
              <span className="text-lg font-black text-indigo-700" data-id="element-11550">
                {sectorAngle}°
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-11551">
              <span className="text-sm font-semibold text-indigo-900" data-id="element-11552">
                Deel:
              </span>
              <span className="text-lg font-black text-indigo-700" data-id="element-11553">
                {sectorAngle}/360
              </span>
            </div>
          </div>
        </div>}

      {/* Measurements card */}
      {mode === 'support' && (propRadius || propDiameter) && !hasSector && <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-5 py-3 shadow-sm" data-id="element-11554">
          <div className="flex flex-col gap-1" data-id="element-11555">
            {propRadius && <div className="flex items-center gap-2" data-id="element-11556">
                <span className="text-sm font-semibold text-indigo-900" data-id="element-11557">
                  Straal (r):
                </span>
                <span className="text-lg font-black text-indigo-700" data-id="element-11558">
                  {radius} cm
                </span>
              </div>}
            {propDiameter && <div className="flex items-center gap-2" data-id="element-11559">
                <span className="text-sm font-semibold text-indigo-900" data-id="element-11560">
                  Diameter (d):
                </span>
                <span className="text-lg font-black text-indigo-700" data-id="element-11561">
                  {diameter} cm
                </span>
              </div>}
          </div>
        </div>}
    </div>;
}